import dayjs from 'dayjs';
import {
  FirestoreDataConverter,
  WithFieldValue,
  DocumentData,
  QueryDocumentSnapshot,
  getFirestore,
  collection,
  doc,
  getDocs,
  Query,
  getDoc,
  DocumentReference,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';

let reportAppError: (error: Error, message: string) => void;

export function setReportAppError(fn: (error: Error, message: string) => void) {
  reportAppError = fn;
}

export type WithId<T> = T & { id: string };

const converter = function converter<T>(): FirestoreDataConverter<WithId<T>> {
  return {
    toFirestore: (data: WithFieldValue<WithId<T>>): DocumentData => {
      return data as DocumentData;
    },
    fromFirestore: (snap: QueryDocumentSnapshot): WithId<T> => {
      return { ...(snap.data() as T), id: snap.id };
    },
  };
};

// helpers
export function getFirebaseCollectionRef<T>(collectionPath: string) {
  return collection(getFirestore(), collectionPath).withConverter(
    converter<T>()
  );
}

export function getFirebaseDocumentRef<T>(docPath: string) {
  return doc(getFirestore(), docPath).withConverter(converter<T>());
}

export async function getFirebaseDocuments<T>(
  query: Query<T>
): Promise<({ id: string } & T)[] | T[]> {
  try {    
    const docsSnap = await getDocs(query);
    return docsSnap.docs.map((docSnap: QueryDocumentSnapshot<T>) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    throw error;
    if (reportAppError)
      reportAppError(error as Error, 'Error getting documents');
    return [];
  }
}

export async function subscribeToFirebaseDocuments<T>(
  query: Query<T>,
  onUpdate: (items: T[]) => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  const unsubscribe = onSnapshot(
    query,
    (snapshot: QuerySnapshot<T>) => {
      const items = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      onUpdate(items);
    },
    (error) => {
      if (reportAppError) reportAppError(error, 'Error getting documents');
      onError(error);
    });
    
  return unsubscribe;
}


export async function getFirebaseDocument<T>(
  docRef: DocumentReference<T>
): Promise<({ id: string } & T) | T | null> {
  try {
    const docSnap = await getDoc(docRef);
    return docSnap.exists()
      ? {
          id: docSnap.id,
          ...docSnap.data(),
        }
      : null;
  } catch (error) {
    if (reportAppError)
      reportAppError(error as Error, 'Error getting documents');
    return null;
  }
}

// FUCK TYPESCRIPT
export function replaceUndefinedWithNull<T>(
  obj: T
): {
  [K in keyof T]: T[K] | null;
} {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
  return Object.keys(obj as any).reduce((acc, key) => {
    const k = key as keyof T;
    acc[k] = obj[k] === undefined ? null : obj[k];
    return acc;
  }, {} as { [K in keyof T]: T[K] | null });
}

export async function setFirebaseDocument<T>(
  docRef: DocumentReference<T>,
  data: T
) {
  try {
    const cleanData = replaceUndefinedWithNull(data);
    // @ts-ignore
    await setDoc(docRef, cleanData);
    return docRef.id;
  } catch (err) {
    if (reportAppError) reportAppError(err as Error, 'Error getting documents');
    return null;
  }
}

export async function updateFirebaseDocument(
  docRef: DocumentReference,
  data: unknown
) {
  try {
    const cleanData = replaceUndefinedWithNull(data);
    // @ts-ignore
    await updateDoc(docRef, cleanData);
    return docRef.id;
  } catch (err) {
    if (reportAppError) reportAppError(err as Error, 'Error getting documents');
    return null;
  }
}

export async function batchSetFirebaseDocuments<T>(
  items: { docRef: DocumentReference<T>; data: T }[]
) {
  try {
    const batch = writeBatch(getFirestore());

    items.forEach(({ docRef, data }) => {
      const cleanData = replaceUndefinedWithNull(data);
      // @ts-ignore
      batch.set(docRef, cleanData);
    });

    await batch.commit();

    return true;
  } catch (err) {
    if (reportAppError) reportAppError(err as Error, 'Error getting documents');
    return false;
  }
}

export async function deleteFirebaseDocument<T>(
  docRef: DocumentReference<T>
): Promise<boolean> {
  try {
    await deleteDoc(docRef);
    return true;
  } catch (err) {
    if (reportAppError) reportAppError(err as Error, 'Error getting documents');
    return false;
  }
}

export const generateDateTimeString = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  const currentDateTimeString = dayjs().format('YYYY-MM-DD-HH-mm');
  let guid = '';
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 8; i++) {
    guid += `${Math.floor(Math.random() * 10)}`;
  }

  return `${currentDateTimeString}-${guid}`;
};
